import { useState, useEffect, useRef } from 'react';
import { useConversation } from '@elevenlabs/react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../utils/cn';
import './chat-animations.css';
import CallIcon from '@mui/icons-material/Call';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import SendIcon from '@mui/icons-material/Send';

const AnesthesiaAIAgent = () => {
  const navigate = useNavigate();
  const [hasPermission, setHasPermission] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // const [currentTranscript, setCurrentTranscript] = useState(""); // removed: not displayed in this UI
  const [messages, setMessages] = useState([]); // { id, source: 'user' | 'ai' | 'system', text }
  const [textInput, setTextInput] = useState("");
  const [micMuted, _setMicMuted] = useState(false);
  // removed isTyping; chat expands only on input focus
  const [_isListening, setIsListening] = useState(false);
  const [aiSpeakingVisual, setAiSpeakingVisual] = useState(false);
  // Keep-alive and deferred action timers
  const keepAliveIntervalRef = useRef(null);
  const keepAliveStopTimeoutRef = useRef(null);
  const waitTimeoutRef = useRef(null);

  // Always use audio for conversation; UI shows text + voice together
  const conversationTextOnly = false;
  const inputRef = useRef(null);
  // Safely extract text payloads from various message shapes
  const extractAiText = (msg) => {
    if (!msg) return '';
    if (typeof msg === 'string') return msg;
    // direct message string (as used in AIAgentSurg360)
    if (typeof msg.message === 'string') return msg.message;
    // streaming deltas or common fields
    if (typeof msg.delta === 'string') return msg.delta;
    if (msg.text) return msg.text;
    if (msg.content) return Array.isArray(msg.content) ? msg.content.join(' ') : msg.content;
    if (msg.transcript) return msg.transcript;
    if (msg.message && (msg.message.text || msg.message.content)) {
      return msg.message.text || msg.message.content;
    }
    if (msg.output && (msg.output.text || msg.output.content)) {
      return msg.output.text || msg.output.content;
    }
    if (msg.response && (msg.response.text || msg.response.content)) {
      return msg.response.text || msg.response.content;
    }
    try {
      const s = JSON.stringify(msg);
      return s.length < 200 ? s : '';
    } catch {
      return '';
    }
  };

  const {
    status,
    isSpeaking,
    startSession,
    endSession,
    sendUserMessage,
    sendUserActivity,
  } = useConversation({
    textOnly: conversationTextOnly,
    micMuted,
    onConnect: () => {
      console.log('Connected to ElevenLabs');
      setIsListening(true);
      setErrorMessage('');
      // Start keep-alive pings for up to 2 minutes to keep session open
      try {
        if (keepAliveIntervalRef.current) clearInterval(keepAliveIntervalRef.current);
        if (keepAliveStopTimeoutRef.current) clearTimeout(keepAliveStopTimeoutRef.current);
        keepAliveIntervalRef.current = setInterval(() => {
          try {
            if (typeof sendUserActivity === 'function') sendUserActivity();
          } catch {
            // no-op
          }
        }, 20000); // ping every 20s
        // Stop pinging after 2 minutes (120s)
        keepAliveStopTimeoutRef.current = setTimeout(() => {
          if (keepAliveIntervalRef.current) {
            clearInterval(keepAliveIntervalRef.current);
            keepAliveIntervalRef.current = null;
          }
        }, 120000);
      } catch {
        // no-op
      }
    },
    onDisconnect: () => {
      console.log('Disconnected from ElevenLabs');
  setIsListening(false);
      // Clear timers
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
        keepAliveIntervalRef.current = null;
      }
      if (keepAliveStopTimeoutRef.current) {
        clearTimeout(keepAliveStopTimeoutRef.current);
        keepAliveStopTimeoutRef.current = null;
      }
      if (waitTimeoutRef.current) {
        clearTimeout(waitTimeoutRef.current);
        waitTimeoutRef.current = null;
      }
    },
    onMessage: (msg) => {
      try {
        const text = extractAiText(msg);
        if (!text) return;

        const rawSource = msg?.source || msg?.role || 'ai';
        const source = rawSource === 'user' || rawSource === 'input' ? 'user' : 'ai';

        // Show live transcript in voice mode too (like Surg360)
  // transcript not shown in this UI; rely on messages list only

        setMessages((prev) => [
          ...prev,
          {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            source,
            text,
          },
        ]);

        // brief visual speaking pulse for the hero orb
        setAiSpeakingVisual(true);
        setTimeout(
          () => setAiSpeakingVisual(false),
          Math.min(2400, Math.max(1200, text.length * 20))
        );
      } catch (e) {
        // no-op
      }
    },
    onError: (err) => {
      const msg = err?.message || String(err || 'Unknown error');
      setErrorMessage(msg);
      console.error('ElevenLabs error:', err);
    },
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (keepAliveIntervalRef.current) clearInterval(keepAliveIntervalRef.current);
      if (keepAliveStopTimeoutRef.current) clearTimeout(keepAliveStopTimeoutRef.current);
      if (waitTimeoutRef.current) clearTimeout(waitTimeoutRef.current);
    };
  }, []);

  // Request microphone permission on component mount (we use audio output even in text UI)
  useEffect(() => {
    const requestMicPermission = async () => {
      try {
    if (!conversationTextOnly) {
          await navigator.mediaDevices.getUserMedia({ audio: true });
          setHasPermission(true);
        } else {
          setHasPermission(true);
        }
      } catch (error) {
        setErrorMessage('Microphone access denied');
        console.error('Error accessing microphone:', error);
      }
    };

    requestMicPermission();
  }, [conversationTextOnly]);

  const handleStartConversation = async () => {
    try {
      // Use environment variable for agent ID
      const agentId = import.meta.env.VITE_ELEVENLABS_ANESTHESIA_AGENT_ID || "agent_01jz2pq5j5e0xszthgq4hw51ea";
      console.log("Starting conversation with agent:", agentId);
      // Request a short-lived WebRTC token from backend
      const base = import.meta.env.VITE_API_BASE_URL || "/api/v1";
      const tokenRes = await fetch(`${base.replace(/\/$/, '')}/elevenlabs/token?agentId=${encodeURIComponent(agentId)}`);
      if (!tokenRes.ok) {
        const msg = await tokenRes.text();
        throw new Error(msg || 'Failed to fetch conversation token');
      }
      const conversationToken = await tokenRes.text();
      const conversationId = await startSession({
        conversationToken,
        connectionType: 'webrtc',
        agentId,
      });
      console.log("Started conversation:", conversationId);
  // keep chat minimized; do not auto-focus input here
    } catch (error) {
      setErrorMessage("Failed to start conversation: " + (error.message || error));
      console.error("Error starting conversation:", error);
    }
  };

  const handleEndConversation = async () => {
    try {
      await endSession();
  // no-op
    } catch (error) {
      setErrorMessage("Failed to end conversation");
      console.error("Error ending conversation:", error);
    }
  };

  // Send a typed message to the agent
  const handleSendText = async () => {
    const value = textInput.trim();
    if (!value) return;
    // Parse "wait" commands like: "wait 1 30 seconds", "wait 90 seconds", "wait 1 minute"
    const parseWaitMs = (raw) => {
      const s = raw.toLowerCase().trim();
      if (!s.startsWith('wait')) return null;
      const rest = s.replace(/^wait\s*/, '');
      // Pattern: "mm ss seconds" e.g., "1 30 seconds"
      let m = rest.match(/^(\d+)\s+(\d+)\s*seconds?/);
      if (m) {
        const minutes = parseInt(m[1], 10);
        const seconds = parseInt(m[2], 10);
        return Math.min((minutes * 60 + seconds) * 1000, 120000);
      }
      // Pattern: "mm:ss"
      m = rest.match(/^(\d+):(\d+)\b/);
      if (m) {
        const minutes = parseInt(m[1], 10);
        const seconds = parseInt(m[2], 10);
        return Math.min((minutes * 60 + seconds) * 1000, 120000);
      }
      // Pattern: "N minutes" | "N min" | "N m"
      m = rest.match(/^(\d+)\s*(minutes?|mins?|m)\b/);
      if (m) {
        const minutes = parseInt(m[1], 10);
        return Math.min(minutes * 60 * 1000, 120000);
      }
      // Pattern: "N seconds" | "N sec" | "N s" | plain "N"
      m = rest.match(/^(\d+)\s*(seconds?|secs?|s)?\b/);
      if (m) {
        const seconds = parseInt(m[1], 10);
        return Math.min(seconds * 1000, 120000);
      }
      return null;
    };
    const waitMs = parseWaitMs(value);
    // Optimistically append the user message
    setMessages((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        source: "user",
        text: value,
      },
    ]);
    setTextInput("");

    try {
      // Auto-connect on first send if not connected
      if (status !== 'connected') {
        const agentId = import.meta.env.VITE_ELEVENLABS_ANESTHESIA_AGENT_ID || "agent_01jz2pq5j5e0xszthgq4hw51ea";
        const base = import.meta.env.VITE_API_BASE_URL || "/api/v1";
        const tokenRes = await fetch(`${base.replace(/\/$/, '')}/elevenlabs/token?agentId=${encodeURIComponent(agentId)}`);
        if (!tokenRes.ok) {
          const msg = await tokenRes.text();
          throw new Error(msg || 'Failed to fetch conversation token');
        }
        const conversationToken = await tokenRes.text();
        await startSession({ conversationToken, connectionType: 'webrtc', agentId });
      }
      if (waitMs !== null) {
        // Do not send the wait command to the agent. Keep session silent and resume later.
        if (waitTimeoutRef.current) clearTimeout(waitTimeoutRef.current);
        waitTimeoutRef.current = setTimeout(() => {
          try {
            if (typeof sendUserMessage === 'function') {
              sendUserMessage('Please ask the last question again.');
            }
          } catch {
            // no-op
          }
        }, waitMs);
      } else {
        if (typeof sendUserMessage === 'function') sendUserMessage(value);
      }
    } catch (err) {
      console.error("Failed to send message:", err);
      setErrorMessage("Failed to send message");
    }
  };

  // Track input focus to control chat window expansion
  const [inputFocused, setInputFocused] = useState(false);

  // Helper to determine if chat should be expanded (only when input is focused)
  const chatExpanded = inputFocused;

  // Only show the message chat window when the user is using text to reply
  const chatVisible = inputFocused || textInput.trim().length > 0;

  // Auto-resize textarea as user types
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = 160; // ~10 lines depending on font
    el.style.height = Math.min(el.scrollHeight, max) + 'px';
  }, [textInput]);

  return (
  <div className="min-h-screen w-full bg-gradient-to-br from-white via-gray-50 to-stone-100 relative md:overflow-hidden overflow-visible">
      {/* Glassmorphism Background with White, Gray, and Beige Diffused Light Effect */}
      <div className="absolute top-0 left-0 w-full h-full">
        {/* Large central diffused light with glassmorphism */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[120vw] h-[120vh] bg-gradient-radial from-white/60 via-gray-100/40 to-transparent rounded-full blur-3xl animate-pulse backdrop-blur-3xl" 
             style={{ animationDuration: '6s' }} />
        
        {/* Multiple layered glassmorphism light effects */}
        <div className="absolute top-20 left-10 w-[600px] h-[600px] bg-gradient-radial from-stone-200/50 via-gray-100/30 to-transparent rounded-full blur-3xl animate-pulse backdrop-blur-2xl" 
             style={{ animationDuration: '4s', animationDelay: '0.5s' }} />
        
        <div className="absolute bottom-20 right-10 w-[700px] h-[700px] bg-gradient-radial from-gray-200/40 via-stone-100/25 to-transparent rounded-full blur-3xl animate-pulse backdrop-blur-xl" 
             style={{ animationDuration: '7s', animationDelay: '1s' }} />
        
        <div className="absolute top-40 right-20 w-96 h-96 bg-gradient-radial from-white/70 via-gray-50/50 to-transparent rounded-full blur-3xl animate-pulse backdrop-blur-2xl" 
             style={{ animationDuration: '5s', animationDelay: '1.5s' }} />
        
        <div className="absolute bottom-40 left-20 w-80 h-80 bg-gradient-radial from-stone-100/60 via-gray-100/40 to-transparent rounded-full blur-3xl animate-pulse backdrop-blur-xl" 
             style={{ animationDuration: '5.5s', animationDelay: '2s' }} />
        
        <div className="absolute top-60 left-1/3 w-72 h-72 bg-gradient-radial from-gray-300/30 via-stone-200/20 to-transparent rounded-full blur-3xl animate-pulse backdrop-blur-lg" 
             style={{ animationDuration: '4.5s', animationDelay: '2.5s' }} />
        
        {/* Floating glassmorphism particles */}
        <div className="absolute top-1/4 left-1/4 w-40 h-40 bg-gradient-radial from-white/50 via-gray-100/30 to-transparent rounded-full blur-2xl animate-bounce backdrop-blur-md" 
             style={{ animationDuration: '8s' }} />
        
        <div className="absolute bottom-1/3 right-1/4 w-48 h-48 bg-gradient-radial from-stone-200/40 via-gray-50/25 to-transparent rounded-full blur-2xl animate-bounce backdrop-blur-md" 
             style={{ animationDuration: '9s', animationDelay: '1s' }} />
        
        <div className="absolute top-3/4 left-2/3 w-36 h-36 bg-gradient-radial from-gray-200/35 to-transparent rounded-full blur-2xl animate-bounce backdrop-blur-sm" 
             style={{ animationDuration: '7s', animationDelay: '2s' }} />
        
        {/* Subtle moving glassmorphism gradients */}
        <div className="absolute inset-0 bg-gradient-to-tr from-white/20 via-gray-100/15 to-stone-50/25 animate-pulse backdrop-blur-sm" 
             style={{ animationDuration: '10s' }} />
        
        <div className="absolute inset-0 bg-gradient-to-bl from-gray-50/15 via-transparent to-stone-200/20 animate-pulse backdrop-blur-sm" 
             style={{ animationDuration: '8s', animationDelay: '3s' }} />
        
        <div className="absolute inset-0 bg-gradient-to-tl from-stone-100/10 via-white/5 to-gray-100/15 animate-pulse backdrop-blur-xs" 
             style={{ animationDuration: '12s', animationDelay: '1.5s' }} />
        
        {/* Glassmorphism overlay with soft texture */}
        <div className="absolute inset-0 bg-white/10 backdrop-blur-[0.5px] border border-white/20" 
             style={{ 
               background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(200,200,200,0.05) 50%, rgba(245,245,220,0.08) 100%)',
               backdropFilter: 'blur(1px) saturate(180%)'
             }} />
      </div>

      {/* Back Button with Glassmorphism */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 z-20 w-12 h-12 bg-white/20 backdrop-blur-md border border-white/30 rounded-full hover:bg-white/30 hover:border-white/40 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center"
        style={{ backdropFilter: 'blur(10px) saturate(180%)' }}
      >
        <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Main Content Container */}
      <div className="flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header pinned to top with subtitle */}
        <div className="absolute top-12 left-1/2 -translate-x-1/2 text-center z-20">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-gray-700 via-gray-600 to-stone-600 bg-clip-text text-transparent">
            Bay Hills FLO
          </h1>
          <p className="text-base sm:text-lg text-gray-600/80 font-medium">Schedule & Assess</p>
        </div>

  {/* Removed legacy 3D Orb button; keeping hero card as primary control */}

        {/* Centered Start Circle and suggestions - middle of viewport */}
        <div className={cn(
          'relative z-10 flex flex-col items-center justify-center min-h-[60vh]'
        )}>
      <button
            onClick={status === 'connected' ? handleEndConversation : handleStartConversation}
            className={cn(
              'group relative w-40 h-40 sm:w-48 sm:h-48 rounded-full overflow-hidden shadow-2xl border border-white/30 bg-white/40 backdrop-blur-xl transition-transform duration-300 hover:scale-105',
        (isSpeaking || aiSpeakingVisual) ? 'orb-speaking orb-ripple ring-0 sm:ring-2 ring-emerald-300/50' : ''
            )}
            style={{
              background:
                'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(245,245,245,0.35) 100%)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5), 0 24px 64px rgba(0,0,0,0.08)'
            }}
            aria-label={status === 'connected' ? 'Stop' : 'Start'}
          >
            <div className="absolute inset-0 bg-gradient-radial from-emerald-200/40 via-transparent to-transparent" />
            <img
              src='./bhlogo.png'
              alt="Start"
              className="relative z-10 w-20 h-20 sm:w-24 sm:h-24 object-contain m-auto translate-y-1 group-hover:translate-y-0 transition-transform duration-300"
            />
          </button>
          <div className="mt-4 text-gray-600 text-sm">
            {status !== 'connected' ? 'Tap to start' : isSpeaking ? 'Speaking…' : 'Connected'}
          </div>

          {/* Suggestion chips (only when not connected) */}
          {status !== 'connected' && (
            <div className="mt-5 flex flex-wrap gap-2 justify-center">
              {['Scheduler', 'FAQs', 'Assessments'].map((label) => (
                <button
                  key={label}
                  onClick={async () => {
                    // Map labels to start commands
                    const cmdMap = {
                      Scheduler: 'start scheduler',
                      FAQs: 'start FAQ',
                      Assessments: 'start assessments',
                    };
                    const cmd = cmdMap[label];
                    if (cmd) {
                      try {
                        if (status !== 'connected') {
                          await handleStartConversation();
                        }
                        // Optimistically show the command as a user message
                        setMessages((prev) => [
                          ...prev,
                          {
                            id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                            source: 'user',
                            text: cmd,
                          },
                        ]);
                        if (typeof sendUserMessage === 'function') {
                          sendUserMessage(cmd);
                        }
                        setTextInput('');
                        return; // prevent fallthrough
                      } catch (e) {
                        setErrorMessage(`Failed to start ${label.toLowerCase()}`);
                        return;
                      }
                    }
                    // Fallback: populate input so user can edit and send
                    if (status !== 'connected') await handleStartConversation();
                    setTextInput(label);
                    // keep chat minimized until user taps the input; no auto-focus
                  }}
                  className="px-3 py-1.5 rounded-full text-xs border border-white/40 bg-white/40 text-gray-700 backdrop-blur-xl hover:bg-white/60 transition"
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

  {/* Bottom-floating Message Overlay (chat + input) - chat hidden unless using text */}
  <div className="fixed inset-x-0 bottom-4 sm:bottom-6 lg:bottom-8 z-30 px-4">
          <div className="mx-auto w-full max-w-md sm:max-w-lg lg:max-w-xl space-y-3">
            {/* Chat window (last 3 messages) */}
            {chatVisible && (
              <div
                className={cn(
                  'rounded-3xl p-3 overflow-hidden shadow-2xl border border-white/20 transition-all duration-300',
                  chatExpanded ? 'h-56 sm:h-64' : 'h-24'
                )}
                style={{
                  background:
                    'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.12) 100%)',
                  backdropFilter: 'blur(24px) saturate(160%)',
                  WebkitBackdropFilter: 'blur(24px) saturate(160%)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25), 0 20px 60px rgba(0,0,0,0.08)'
                }}
              >
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-xs text-gray-600 text-center">Start chatting by typing a message below.</p>
                  </div>
                ) : (
                  <div className="flex flex-col justify-end h-full space-y-2">
                    {messages.slice(-3).map((m, index) => (
                      <div
                        key={m.id}
                        className={cn(
                          'flex slide-in-animation',
                          m.source === 'user' ? 'justify-end' : 'justify-start'
                        )}
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        <div
                          className={cn(
                            'px-2.5 py-1.5 text-xs max-w-[80%] rounded-xl transform transition-all duration-300',
                            m.source === 'user'
                              ? 'text-white shadow-[0_4px_12px_rgba(37,99,235,0.25)]'
                              : 'text-gray-800 border border-white/30 shadow-sm'
                          )}
                          style={
                            m.source === 'user'
                              ? {
                                  background:
                                    'linear-gradient(135deg, rgba(37,99,235,0.85) 0%, rgba(99,102,241,0.85) 100%)',
                                }
                              : {
                                  background: 'rgba(255,255,255,0.5)',
                                  backdropFilter: 'blur(16px) saturate(180%)',
                                  WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                                }
                          }
                        >
                          <span className="block opacity-70 text-[8px] mb-0.5 uppercase tracking-wide">
                            {m.source === 'user' ? 'You' : m.source === 'ai' ? 'AI' : 'System'}
                          </span>
                          <span className="leading-tight">{m.text}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Input row */}
            <div
              className={cn(
                'relative flex items-end gap-2 rounded-2xl p-2 shadow-2xl border border-white/30',
                status === 'connected' && !isSpeaking
                  ? 'ring-2 ring-emerald-300/70 shadow-[0_0_24px_rgba(16,185,129,0.45)]'
                  : ''
              )}
              style={{
                background:
                  'linear-gradient(135deg, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0.4) 100%)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              }}
            >
              <textarea
                value={textInput}
                onChange={(e) => {
                  setTextInput(e.target.value);
                  try {
                    if (typeof sendUserActivity === 'function') sendUserActivity();
                  } catch {
                    /* no-op */
                  }
                }}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendText();
                  }
                }}
                placeholder={status === 'connected' ? 'Type your message…' : 'Type to start and connect…'}
                className="flex-1 bg-transparent outline-none text-xs text-gray-800 placeholder:text-gray-400 px-3 py-2 pr-[22%] min-h-[40px] max-h-40 overflow-y-auto"
                ref={inputRef}
                rows={1}
                style={{ resize: 'none' }}
              />
              {/* Button stack floating within right 20% of the input area */}
              <div className="absolute inset-y-2 right-2 w-[20%] flex items-end justify-end gap-2">
                {status === 'connected' ? (
                  <button
                    onClick={handleEndConversation}
                    className="px-3 py-2 rounded-xl transition bg-red-100 text-red-700 hover:bg-red-200 border border-red-300"
                    title="Disconnect"
                    aria-label="Disconnect"
                  >
                    <PhoneDisabledIcon fontSize="small" />
                  </button>
                ) : (
                  <button
                    onClick={handleStartConversation}
                    className="px-3 py-2 rounded-xl transition bg-emerald-600 text-white hover:bg-emerald-500"
                    title="Connect"
                    aria-label="Connect"
                  >
                    <CallIcon fontSize="small" />
                  </button>
                )}
                <button
                  onClick={handleSendText}
                  disabled={!textInput.trim()}
                  className={cn(
                    'p-2 rounded-xl transition',
                    textInput.trim()
                      ? 'bg-gray-900 text-white hover:bg-gray-800'
                      : 'bg-gray-300/60 text-gray-500 cursor-not-allowed'
                  )}
                  title="Send"
                  aria-label="Send"
                >
                  <SendIcon fontSize="small" />
                </button>
              </div>
            </div>
          </div>
        </div>
  


        {/* Status Indicators with Glassmorphism */}
        <div className="flex flex-col items-center space-y-4 w-full max-w-md relative z-10">

          {/* Error Messages */}
          {errorMessage && (
            <div className="bg-red-50/60 backdrop-blur-lg border border-red-200/40 rounded-xl px-6 py-3 shadow-lg"
                 style={{ backdropFilter: 'blur(15px) saturate(180%)' }}>
              <p className="text-sm sm:text-base text-red-700">{errorMessage}</p>
            </div>
          )}

          {/* Permission Message */}
          {!hasPermission && !conversationTextOnly && (
            <div className="bg-amber-50/60 backdrop-blur-lg border border-amber-200/40 rounded-xl px-6 py-3 text-center shadow-lg"
                 style={{ backdropFilter: 'blur(15px) saturate(180%)' }}>
              <p className="text-sm sm:text-base text-amber-700">Please allow microphone access to start</p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AnesthesiaAIAgent;